// src/components/Layout/MainLayout.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Menu, Plus, Search, LogOut, Sun, Moon, X, Settings, MessageSquare } from 'lucide-react'; // 👈 補上 MessageSquare
import { ChatModal } from '../Chat/ChatModal'; // 👈 引入 ChatModal
import { useChat } from '../../hooks/useChat';

interface MainLayoutProps {
  children?: React.ReactNode;
  currentUserId?: string;
  onLogout: () => void;
  onCreateNote: () => void;
  onEditNote: (note: any) => void;
  onShareNote: (note: any) => void;
  onFilterType: (type: string | null) => void;
  currentFilter: string | null;
  username?: string;
  allNotes: any[];
  notes: any[];
  loading: boolean;
  deleteNote: (id: string) => Promise<void>;
  togglePin: (id: string) => Promise<void>;
  toggleArchive: (id: string) => Promise<void>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchResultCount: number;
  onOpenSettings: () => void;
  receivedTotal?: number;
  receivedAccepted?: number;
  receivedPendingCount?: number;
  myShareCount?: number;
  myAcceptedCount?: number;
  sharedPendingCount?: number;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  currentUserId,
  onLogout,
  onCreateNote,
  onEditNote,
  onShareNote,
  onFilterType,
  currentFilter,
  username,
  allNotes,
  notes,
  loading,
  deleteNote,
  togglePin,
  toggleArchive,
  searchQuery,
  onSearchChange,
  searchResultCount,
  onOpenSettings,
  receivedTotal = 0,
  receivedAccepted = 0,
  receivedPendingCount = 0,
  myShareCount = 0,
  myAcceptedCount = 0,
  sharedPendingCount = 0,
}) => {
  const [isDark, setIsDark] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (window.innerWidth < 1024 && isSidebarOpen) {
        const target = e.target as HTMLElement;
        if (sidebarRef.current && !sidebarRef.current.contains(target)) {
          setIsSidebarOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  const handleMainClick = () => {
    if (window.innerWidth < 1024 && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  const totalCount = allNotes.length;
  const memoCount = allNotes.filter(n => n.type === 'memo').length;
  const passwordCount = allNotes.filter(n => n.type === 'password').length;
  const todoCount = allNotes.filter(n => n.type === 'todo').length;

  const filterOptions = [
    { key: null, label: '📝 全部筆記', count: totalCount },
    { key: 'memo', label: '📋 記事', count: memoCount },
    { key: 'password', label: '🔒 密碼', count: passwordCount },
    { key: 'todo', label: '✅ 待辦', count: todoCount },
  ];

  // ✅ 計算是否有未處理的分享（用於紅點）
  const hasPendingShares = receivedPendingCount > 0 || sharedPendingCount > 0;

	const [hasUnreadChat, setHasUnreadChat] = useState(false);

  useEffect(() => {
    if (!currentUserId) return;

    const checkUnread = async () => {
      try {
        const res = await fetch(`/api/chat/rooms?userId=${currentUserId}&_t=${Date.now()}`);
        if (res.ok) {
          const data = await res.json();
          // 直接使用後端計算的紅點狀態
          setHasUnreadChat(Boolean(data.hasUnreadTotal));
        }
      } catch (err) {
        console.error('輪詢未讀訊息失敗:', err);
      }
    };

    checkUnread();
    const timer = setInterval(checkUnread, 3000);

    return () => clearInterval(timer);
  }, [currentUserId]);

  const handleOpenChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsChatOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" onClick={handleMainClick}>
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 min-w-0">
        <div className="flex items-center justify-between px-4 h-16 overflow-hidden">
          {/* 左側 */}
          <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
            {/* ✅ Menu 按鈕 + 紅點 */}
            <button
              onClick={(e) => { e.stopPropagation(); setIsSidebarOpen(!isSidebarOpen); }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg lg:hidden flex-shrink-0 relative"
            >
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              {/* ✅ 紅點：有未處理的分享時顯示 */}
							{(receivedPendingCount > 0 || sharedPendingCount > 0) && (
								<span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
									<span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping"></span>
									<span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
								</span>
							)}
            </button>
            
            <h1 className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">NOTEBOOK++</h1>
            {username && (
              <span className="text-sm text-gray-500 dark:text-gray-400 hidden md:inline truncate">
                👤 {username}
              </span>
            )}
          </div>

          {/* 右側按鈕 */}
          <div className="flex items-center justify-end gap-1 md:gap-2 flex-shrink-0 min-w-0">
            {/* 桌面版搜尋框 */}
            <div className="hidden md:flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-1.5 transition-all focus-within:ring-2 focus-within:ring-blue-500">
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="搜尋標題/內容/標籤..."
                className="bg-transparent border-none outline-none px-2 text-sm text-gray-700 dark:text-gray-200 w-40 lg:w-64"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {searchQuery && (
              <span className="hidden md:inline text-xs text-gray-400 flex-shrink-0">
                {searchResultCount} 筆
              </span>
            )}

            {/* 新增筆記 */}
            <button
              onClick={onCreateNote}
              className="p-2 md:p-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white flex-shrink-0"
            >
              <Plus className="w-6 h-6 md:w-6 md:h-6" />
            </button>

            {/* 主題切換 */}
            <button
              onClick={toggleTheme}
              className="p-1.5 md:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
            >
              {isDark ? (
                <Sun className="w-5 h-5 md:w-5 md:h-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 md:w-5 md:h-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>

						{/* 即時聊天按鈕 */}
						<button
							onClick={handleOpenChat}
							className="p-1.5 md:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0 relative"
							title="即時聊天"
						>
							<MessageSquare className="w-5 h-5 md:w-5 md:h-5 text-gray-600 dark:text-gray-300" />

							{/* 🔴 未讀紅點 */}
							{hasUnreadChat && (
								<span className="absolute top-1 right-1 flex h-2.5 w-2.5">
									<span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
									<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500"></span>
								</span>
							)}
						</button>

            {/* 設定 */}
            <button
              onClick={onOpenSettings}
              className="p-1.5 md:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
              title="設定"
            >
              <Settings className="w-5 h-5 md:w-5 md:h-5 text-gray-600 dark:text-gray-300" />
            </button>

            {/* 登出 */}
            <button
              onClick={onLogout}
              className="p-1.5 md:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
            >
              <LogOut className="w-5 h-5 md:w-5 md:h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          ref={sidebarRef}
          className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 mt-16 lg:mt-0 h-[calc(100vh-4rem)]`}
        >
          <div className="p-4 space-y-4 overflow-y-auto h-full">
            {/* 篩選 */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">篩選</h3>
              <div className="space-y-1">
                {filterOptions.map((opt) => (
                  <button
                    key={opt.key || 'all'}
                    onClick={() => { onFilterType(opt.key); if (window.innerWidth < 1024) setIsSidebarOpen(false); }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm ${
                      currentFilter === opt.key
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="truncate">{opt.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                      currentFilter === opt.key
                        ? 'bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                    }`}>
                      {opt.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* 分享區塊 */}
            <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">分享</h3>

              {/* 分享給我 - 紅點已存在 */}
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openShareList', { detail: { tab: 'received' } }));
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  📥 分享給我
                  {/* ✅ 紅點：有待處理的分享 */}
                  {receivedPendingCount > 0 && (
                    <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                  )}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400">
                  {receivedTotal}
                </span>
              </button>

              {/* 我分享的 - 紅點已存在 */}
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openShareList', { detail: { tab: 'shared' } }));
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  📤 我分享的
                  {/* ✅ 紅點：有待回應的分享 */}
                  {sharedPendingCount > 0 && (
                    <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                  )}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400">
                  {myShareCount}
                </span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto h-[calc(100vh-4rem)] min-w-0" onClick={handleMainClick}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 dark:text-gray-400">載入中...</div>
            </div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="text-6xl mb-4">{searchQuery ? '🔍' : '📝'}</div>
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
                {searchQuery ? '找不到符合的筆記' : '還沒有筆記'}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                {searchQuery ? '試試其他關鍵字' : '點擊右上角的 + 按鈕建立'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {notes.map((note) => (
                <div
                  key={note.id}
                  onClick={() => onEditNote(note)}
                  className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer ${note.isPinned ? 'border-l-4 border-blue-500' : ''}`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{note.type === 'password' ? '🔒' : note.type === 'todo' ? '✅' : '📝'}</span>
                          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{note.title}</h3>
                        </div>
                        {note.type === 'password' && note.username && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">👤 {note.username}</p>
                        )}
                        {note.type === 'todo' && (
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {note.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                note.status === '待處理' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                note.status === '進行中' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                note.status === '已完成' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                              }`}>{note.status}</span>
                            )}
                            {note.due_date && (
                              <span className="text-xs text-gray-400">📅 {note.due_date}</span>
                            )}
                          </div>
                        )}
                        {note.type === 'memo' && note.content && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{note.content}</p>
                        )}
                      </div>

                      {/* 操作按鈕 */}
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditNote(note);
                          }}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-blue-500"
                          title="編輯"
                        >
                          ✏️
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onShareNote(note);
                          }}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-green-500"
                          title="分享"
                        >
                          📤
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePin(note.id);
                          }}
                          className={`p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${note.isPinned ? 'text-yellow-500' : 'text-gray-400'}`}
                          title={note.isPinned ? '取消釘選' : '釘選'}
                        >
                          📌
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('確定要刪除這篇筆記嗎？此操作無法復原。')) {
                              deleteNote(note.id);
                            }
                          }}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-red-400 hover:text-red-600 transition-colors"
                          title="刪除"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    {/* 標籤 */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300">{note.category}</span>
                      <span className="text-xs text-gray-400">{note.date}</span>
                      {note.tags.map((tag: string) => (
                        <span key={tag} className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">#{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

{/* 手機版底部搜尋 */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="搜尋筆記..."
            className="flex-1 bg-transparent border-none outline-none px-2 text-sm text-gray-700 dark:text-gray-200"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ✅ 正確位置：放在 md:hidden 外部的最外層 */}
      {currentUserId && (
        <ChatModal
          currentUserId={currentUserId}
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
        />
      )}
    </div>
  );
};